import { Errors } from "./errors"
import axios from "axios"

type FormDataObject = {
  [key: string]: string | number | boolean | null | Object
}

export class Form {
  private originalData: FormDataObject
  public errors: Errors
  public success?: boolean;
  [key: string]: any;

  /**
   * Create a new Form instance.
   *
   * @param {object} data - The form data
   * @param {boolean} disableReset - Whether to disable the reset functionality
   */
  constructor (data: FormDataObject, disableReset?: boolean) {
    this.originalData = data

    for (const field in data) {
      this[field] = data[field]
    }

    if (disableReset === true) {
      this.reset = (): void => {}
    }

    this.errors = new Errors()
  }

  /**
   * Fetch all relevant data for the form.
   */
  data (): Record<string, any> {
    const data: Record<string, any> = {}

    for (const property in this.originalData) {
      data[property] = this[property]
    }

    return data
  }

  /**
   * Reset the form fields.
   */
  reset (): void {
    for (const field in this.originalData) {
      this[field] = ""
    }

    this.errors.clear()
  }

  /**
   * Send a POST request to the given URL.
   *
   * @param {string} url - The URL to send the request to
   */
  post (url: string): Promise<any> {
    return this.submit("post", url)
  }

  /**
   * Send a PUT request to the given URL.
   *
   * @param {string} url - The URL to send the request to
   */
  put (url: string): Promise<any> {
    return this.submit("put", url)
  }

  /**
   * Send a PATCH request to the given URL.
   *
   * @param {string} url - The URL to send the request to
   */
  patch (url: string): Promise<any> {
    return this.submit("patch", url)
  }

  /**
   * Send a DELETE request to the given URL.
   *
   * @param {string} url - The URL to send the request to
   */
  delete (url: string): Promise<any> {
    return this.submit("delete", url)
  }

  /**
   * Submit the form.
   *
   * @param {string} requestType - The HTTP method to use
   * @param {string} url - The URL to send the request to
   */
  submit (requestType: string, url: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const request = (() => {
        switch (requestType) {
          case "get": return axios.get
          case "post": return axios.post
          case "put": return axios.put
          case "patch": return axios.patch
          case "delete": return axios.delete
          default: throw new Error(`Unsupported request type: ${requestType}`)
        }
      })()
      request(url, this.data())
        .then(response => {
          this.onSuccess()
          resolve(response.data)
        })
        .catch(error => {
          this.onFail(error.response.data.errors)
          reject(error.response.data.errors || error.response.data.message)
        })
    })
  }

  /**
   * Handle a successful form submission.
   */
  onSuccess (): void {
    this.reset()
    this.success = true
  }

  /**
   * Handle a failed form submission.
   *
   * @param {object} errors - The errors returned from the server
   */
  onFail (errors: any): void {
    this.errors.record(errors)
  }
}
